import Vue from 'vue';
import FreeDraw, { NONE, ALL } from 'leaflet-freedraw';
import { propsBinder, findRealParent } from 'vue2-leaflet';
import { debounce, isEqual } from 'lodash-es';

function withNonReactive(data) {
  return () => data;
}

function validateMode(mode) {
  if (mode < 0 || mode > ALL) {
    return false;
  }

  return true;
}

const props = {
  options: {
    type: Object,

    default() {
      return {};
    }

  },
  mode: {
    type: Number,
    default: ALL,
    custom: true,

    validator(value) {
      return validateMode(value);
    }

  },
  value: {
    type: Array,
    custom: true,
    default: () => []
  },
  debounce: {
    type: Boolean,
    default: true,
    custom: true
  }
};
var script = Vue.extend({
  name: 'LFreeDraw',
  props,

  data() {
    return withNonReactive({
      ready: false,
      lastSetPolygons: []
    })();
  },

  mounted() {
    const noParent = 'l-freedraw needs to be a children or grand children of a l-map object';

    if (this.$parent) {
      this.mapObject = new FreeDraw({ ...this.options,
        mode: this.mode
      });
      this.mapObject.on(this.$listeners);
      propsBinder(this, this.mapObject, props);
      const debouncedHandler = debounce(this.markerHandler, 100);

      if (this.debounce) {
        this.mapObject.on('markers', debouncedHandler);
      } else {
        this.mapObject.on('markers', this.markerHandler);
      }

      this.parentContainer = findRealParent(this.$parent);

      if (this.parentContainer) {
        this.parentContainer.addLayer(this);
        this.setValue(this.value);
        this.ready = true;
        this.$nextTick(() => {
          this.$emit('ready', this.mapObject);
        });
      } else {
        throw new Error(noParent);
      }
    } else {
      throw new Error(noParent);
    }
  },

  beforeDestroy() {
    if (this.mapObject) {
      this.mapObject.off('markers');
      this.mapObject.off(this.$listeners);
      this.mapObject.clear();
      this.setMode(NONE);

      if (this.parentContainer) {
        this.parentContainer.removeLayer(this);
      }
    }
  },

  methods: {
    setValue(newPolygons) {
      if (!newPolygons) {
        this.lastSetPolygons = [];

        if (this.mapObject) {
          this.mapObject.clear();
        }
      } else if (!isEqual(newPolygons, this.lastSetPolygons)) {
        this.lastSetPolygons = newPolygons;

        if (this.mapObject) {
          this.mapObject.clear();
          newPolygons.forEach(polygon => this.mapObject && this.mapObject.create(polygon));
        }
      }
    },

    markerHandler(event) {
      const newPolygons = event.latLngs;

      if (!isEqual(newPolygons, this.lastSetPolygons)) {
        this.$emit('input', event.latLngs);
        this.lastSetPolygons = event.latLngs;
      }
    },

    setMode(mode) {
      if (!validateMode(mode)) {
        throw new Error('Invalid mode');
      } else if (this.mapObject) {
        this.mapObject.mode(mode);
      }
    },

    setDebounce(value) {
      if (this.mapObject) {
        if (value) {
          this.mapObject.off('markers');
          const debouncedHandler = debounce(this.markerHandler, 100);
          this.mapObject.on('markers', debouncedHandler);
        } else {
          this.mapObject.off('markers');
          this.mapObject.on('markers', this.markerHandler);
        }
      }
    }

  }
});

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div');
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = undefined;
/* scoped */

const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, undefined, undefined, undefined);

// Import vue component

// Default export is installable instance of component.
// IIFE injects install function into component, allowing component
// to be registered via Vue.use() as well as Vue.component(),
var entry_esm = /* #__PURE__ */(() => {
  // Assign InstallableComponent type
  const installable = __vue_component__; // Attach install function executed by Vue.use()

  installable.install = Vue => {
    Vue.component('LFreedraw', installable);
  };

  return installable;
})(); // It's possible to expose named exports when writing components that can
// also be used as directives, etc. - eg. import { RollupDemoDirective } from 'rollup-demo';
// export const RollupDemoDirective = directive;

export default entry_esm;
